import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import {
  useGetConversation,
  useHasConversationBeenInitiated,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setChatVisibility } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { RootState } from '../../../../app';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import ConversationsChat from './ConversationsChat';
import ConversationsChatHeader from './ConversationsChatHeader';
import {
  AbsoluteAllMessagesContainer,
  ConversationsContainer,
} from './ConversationsChatbox.styles';

const ConversationsChatBox = () => {
  const dispatch = useDispatch();
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const preconfiguredMessages = useSelector(
    (state: RootState) => state.preconfiguredMessages?.messages,
  );
  const enabledPreconfiguredMessages = preconfiguredMessages?.filter(
    message => message.enabled,
  );

  const isConversationStarted = useHasConversationBeenInitiated(callerID);
  const tcapiConversation = useGetConversation(callerID);
  const [isAllMessagesVisible, setIsAllMessagesVisible] = useState(false);
  const [selectedPreconfiguredMessage, setSelectedPreconfiguredMessage] =
    useState<PreconfiguredMessageInterface | null>(null);

  // Check if conversation timed out (never accepted)
  const isTcapiTimedOut = useMemo(() => {
    if (
      !tcapiConversation ||
      tcapiConversation.source !== SOURCE_TCAPI ||
      !tcapiConversation.data
    ) {
      return false;
    }
    return tcapiConversation.data.acceptStatus === 'timed_out';
  }, [tcapiConversation]);

  // Use Redux state for chat visibility to maintain state across tab navigation
  // Force collapse if conversation timed out (was never accepted)
  const isChatBoxVisible = useSelector((state: any) =>
    isTcapiTimedOut
      ? false
      : (state.eventPane?.chatVisibility?.isConversationsChatVisible ??
        isConversationStarted),
  );

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
  };

  const handleCopyToChat = (selectedMessage: PreconfiguredMessageInterface) => {
    setIsAllMessagesVisible(false);
    setSelectedPreconfiguredMessage(selectedMessage);
  };

  const setIsChatBoxVisible = useCallback(
    (isVisible: boolean) => {
      dispatch(
        setChatVisibility({
          chatType: 'isConversationsChatVisible',
          isVisible,
        }),
      );
    },
    [dispatch],
  );

  return (
    <ConversationsContainer
      data-name="conversations-container"
      flex={isChatBoxVisible ? '1' : '0'}
    >
      <ConversationsChatHeader
        isChatBoxVisible={isChatBoxVisible}
        setIsChatBoxVisible={setIsChatBoxVisible}
        isConversationStarted={isConversationStarted}
      />
      {isConversationStarted && (
        <ConversationsChat
          isChatBoxVisible={isChatBoxVisible}
          setIsAllMessagesVisible={setIsAllMessagesVisible}
          selectedPreconfiguredMessage={selectedPreconfiguredMessage}
          setSelectedPreconfiguredMessage={setSelectedPreconfiguredMessage}
        />
      )}
      {isAllMessagesVisible && (
        <AbsoluteAllMessagesContainer>
          <AllMessages
            preconfiguredMessages={enabledPreconfiguredMessages}
            onClose={handleViewClose}
            onCopyToChat={handleCopyToChat}
          />
        </AbsoluteAllMessagesContainer>
      )}
    </ConversationsContainer>
  );
};

export default ConversationsChatBox;
